import { Button, notify } from '@affine/component';
import { FetchService } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

import * as styles from './setting-panel.css';

export interface LinearConnectionSummary {
  organizationId: string;
  organizationName: string | null;
  state: 'ok' | 'reconnect_required' | 'unavailable';
}

interface Binding {
  workspaceId: string;
  organizationId: string;
  projectId: string;
  /** Resolved live from Linear; null when there is no grant to resolve it. */
  projectName?: string | null;
}

interface Project {
  id: string;
  name: string;
}

interface Team {
  id: string;
  name: string;
  key?: string;
}

/**
 * Which Linear project THIS workspace tracks.
 *
 * Distinct from the connections list above it: that is account-level (which
 * Linear workspaces the developer has authorised), this is per-workspace (which
 * project this contract's work lives in). A developer holds one Linear
 * workspace per contract, so both surfaces are needed and they answer different
 * questions.
 */
export const LinearWorkspaceBinding = ({
  connections,
}: {
  connections: LinearConnectionSummary[];
}) => {
  const t = useI18n();
  const fetchService = useService(FetchService);
  const workspaceId = useService(WorkspaceService).workspace.id;

  const [binding, setBinding] = useState<Binding | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Only organizations with a working grant can be bound — the backend refuses
  // the rest, so offering them would produce a guaranteed failure.
  const usable = connections.filter(c => c.state === 'ok');
  const [orgId, setOrgId] = useState<string>(usable[0]?.organizationId ?? '');
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamId, setTeamId] = useState<string>('');
  const [name, setName] = useState('');
  const [useExisting, setUseExisting] = useState(false);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [projectId, setProjectId] = useState('');

  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      const res = await fetchService.fetch(
        `/api/linear/binding?workspaceId=${encodeURIComponent(workspaceId)}`
      );
      const data = await res.json();
      if (requestIdRef.current !== requestId) return;
      setBinding(data.binding ?? null);
    } catch (error) {
      if (requestIdRef.current !== requestId) return;
      notify.error({
        title: t['com.affine.linear.project.load-failed'](),
        message: error instanceof Error ? error.message : 'Please try again',
      });
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [fetchService, workspaceId, t]);

  useEffect(() => {
    refresh().catch(console.error);
  }, [refresh, t]);

  // Teams are per-organization, so this refetches whenever the picker changes.
  useEffect(() => {
    if (!orgId || binding) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchService.fetch(
          `/api/linear/binding/teams?organizationId=${encodeURIComponent(orgId)}`
        );
        const data = await res.json();
        if (cancelled) return;
        setTeams(data.teams ?? []);
        setTeamId(data.teams?.[0]?.id ?? '');
      } catch (error) {
        if (cancelled) return;
        // Not fatal: the section still renders, it just has no teams to offer,
        // and the existing-project path remains available.
        notify.error({
          title: t['com.affine.linear.project.teams-failed'](),
          message: error instanceof Error ? error.message : 'Please try again',
        });
      }
    })().catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [fetchService, orgId, binding, t]);

  // Only when the developer actually opens the existing-project path: most
  // bindings are new projects, and this is a Linear call against their own
  // rate limit (YOD-592).
  useEffect(() => {
    if (!orgId || binding || !useExisting) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchService.fetch(
          `/api/linear/binding/projects?organizationId=${encodeURIComponent(orgId)}`
        );
        const data = await res.json();
        if (cancelled) return;
        const list: Project[] = data.projects ?? [];
        setProjects(list);
        setProjectId(list[0]?.id ?? '');
      } catch (error) {
        if (cancelled) return;
        // An empty list is a real answer; a failure is not. Distinguish them so
        // the raw-id escape hatch still makes sense.
        setProjects(null);
        notify.error({
          title: t['com.affine.linear.project.projects-failed'](),
          message: error instanceof Error ? error.message : 'Please try again',
        });
      }
    })().catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [fetchService, orgId, binding, useExisting, t]);

  const post = useCallback(
    async (body: Record<string, unknown>) => {
      setBusy(true);
      try {
        await fetchService.fetch('/api/linear/binding', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ workspaceId, organizationId: orgId, ...body }),
        });
        notify.success({ title: t['com.affine.linear.project.linked']() });
        await refresh();
      } catch (error) {
        notify.error({
          title: t['com.affine.linear.project.link-failed'](),
          message: error instanceof Error ? error.message : 'Please try again',
        });
      } finally {
        setBusy(false);
      }
    },
    [fetchService, workspaceId, orgId, refresh, t]
  );

  const handleUnlink = useCallback(() => {
    setBusy(true);
    fetchService
      .fetch(
        `/api/linear/binding?workspaceId=${encodeURIComponent(workspaceId)}`,
        { method: 'DELETE' }
      )
      .then(() => {
        notify.success({ title: t['com.affine.linear.project.unlinked']() });
        return refresh();
      })
      .catch((error: unknown) => {
        notify.error({
          title: t['com.affine.linear.project.unlink-failed'](),
          message: error instanceof Error ? error.message : 'Please try again',
        });
      })
      .finally(() => setBusy(false));
  }, [fetchService, workspaceId, refresh, t]);

  if (loading) return null;

  // --- already bound -------------------------------------------------------

  if (binding) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          {t['com.affine.linear.project.title']()}
        </div>
        <div className={styles.row}>
          <div className={styles.rowMain}>
            <span className={styles.workspaceName}>
              {/* The id is the fallback, not the answer: it tells the developer
                  nothing about WHICH project is bound (YOD-590). */}
              {binding.projectName ?? binding.projectId}
            </span>
            <span className={styles.rowDetail}>
              {t['com.affine.linear.project.bound']()}
            </span>
          </div>
          <Button variant="error" onClick={handleUnlink} disabled={busy}>
            {t['com.affine.linear.project.unlink']()}
          </Button>
        </div>
        <div className={styles.hint}>
          {t['com.affine.linear.project.unlink-hint']()}
        </div>
      </div>
    );
  }

  // --- nothing connected ---------------------------------------------------

  if (usable.length === 0) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          {t['com.affine.linear.project.title']()}
        </div>
        <div className={styles.hint}>
          {t['com.affine.linear.project.connect-first']()}
        </div>
      </div>
    );
  }

  // --- the picker ----------------------------------------------------------

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {t['com.affine.linear.project.title']()}
      </div>
      <div className={styles.hint}>
        {t['com.affine.linear.project.choose']()}
      </div>

      {usable.length > 1 ? (
        <select
          className={styles.select}
          aria-label={t['com.affine.linear.project.workspace-label']()}
          value={orgId}
          onChange={e => setOrgId(e.target.value)}
        >
          {usable.map(c => (
            <option key={c.organizationId} value={c.organizationId}>
              {c.organizationName ?? c.organizationId}
            </option>
          ))}
        </select>
      ) : null}

      {useExisting ? (
        <>
          {projects === null ? (
            // The list could not be fetched. The id field is the escape hatch
            // rather than the default, so the developer is not stuck.
            <input
              className={styles.input}
              placeholder={t['com.affine.linear.project.id-placeholder']()}
              value={projectId}
              onChange={e => setProjectId(e.target.value)}
            />
          ) : projects.length === 0 ? (
            <div className={styles.hint}>
              {t['com.affine.linear.project.none']()}
            </div>
          ) : (
            <select
              className={styles.select}
              aria-label={t['com.affine.linear.project.project-label']()}
              value={projectId}
              onChange={e => setProjectId(e.target.value)}
            >
              {projects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <div className={styles.actions}>
            <Button
              variant="primary"
              disabled={busy || !projectId.trim()}
              onClick={() => {
                post({ projectId: projectId.trim() }).catch(console.error);
              }}
            >
              {t['com.affine.linear.project.link']()}
            </Button>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setUseExisting(false)}
            >
              {t['com.affine.linear.project.create-instead']()}
            </button>
          </div>
        </>
      ) : (
        <>
          <select
            className={styles.select}
            aria-label={t['com.affine.linear.project.team-label']()}
            value={teamId}
            onChange={e => setTeamId(e.target.value)}
          >
            {teams.map(t => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <input
            className={styles.input}
            placeholder={t['com.affine.linear.project.name-placeholder']()}
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <div className={styles.actions}>
            <Button
              variant="primary"
              disabled={busy || !teamId}
              onClick={() => {
                post({ teamId, name: name.trim() || undefined }).catch(
                  console.error
                );
              }}
            >
              {t['com.affine.linear.project.create']()}
            </Button>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setUseExisting(true)}
            >
              {t['com.affine.linear.project.use-existing']()}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
